import { Cancel01Icon, Tick02Icon } from '@hugeicons/react'

import { AppIcon } from '@/shared/ui/icons'
import { RewardBadge } from '@/shared/ui/RewardBadge'

import { LevelPlayInterstitial } from './LevelPlayInterstitial'

/**
 * Tela de conclusão da execução, servida aos dois domínios.
 *
 * Era a maior divergência da change (201 linhas normalizadas) e o merge tirou
 * coisa boa de cada lado:
 *
 * - da **jornada**: `RewardBadge` do Design System, onde o quiz reimplementava a
 *   exibição de recompensas em 40+ linhas de grid e divs à mão; e os ícones de
 *   acerto/erro por import direto, no lugar de slug de `AppIcon`
 * - do **quiz**: a cor por faixa de desempenho. A jornada usava `text-red-500`
 *   nas quatro faixas, inclusive na de acerto total — vermelho para quem acertou
 *   tudo é defeito, não escolha. E o fallback quando nenhuma faixa casa, sem o
 *   qual um percentual acima de 1 estoura o componente
 *
 * O que difere de fato entre os domínios — origem do percentual, das recompensas
 * e da lista de acertos, e para onde o participante vai depois — entra por
 * props. Nada aqui sabe se está numa jornada ou num quiz.
 */

/** Faixas de desempenho, da pior para a melhor. A primeira que couber vence. */
const TIERS = [
  {
    score: 0,
    icon: 'face-sad-sweat',
    iconClassName: 'text-danger',
    title: 'Não desanime!',
    description: 'Você não concluiu nenhuma atividade'
  },
  {
    score: 0.4,
    icon: 'face-smile-beam',
    iconClassName: 'text-warning',
    title: 'Você está no caminho!',
    description: 'Você concluiu algumas atividades'
  },
  {
    score: 0.7,
    icon: 'face-party',
    iconClassName: 'text-brand',
    title: 'Muito bem!',
    description: 'Você concluiu a maioria das atividades'
  },
  {
    score: 1,
    icon: 'face-smile-hearts',
    iconClassName: 'text-success',
    title: 'Excelente!',
    description: 'Você concluiu todas as atividades'
  }
]

export interface LevelPlayFinishRewards {
  acquiredPoints?: number
  totalPoints?: number
  acquiredCoins?: number
  totalCoins?: number
}

export interface LevelPlayFinishItem {
  id: string | number
  name: string
  correct: boolean
}

export interface LevelPlayFinishProps {
  /** Percentual de acerto, de 0 a 1. */
  correctPercent: number
  rewards?: LevelPlayFinishRewards
  /**
   * Quando o recurso permite respostas erradas, o participante vê o total ao
   * lado do obtido e a lista de acertos.
   */
  showBreakdown?: boolean
  /** Lista normalizada de atividades e se foram acertadas. */
  items?: LevelPlayFinishItem[]
  /** Navegação de saída — cada domínio tem a sua. */
  actions?: React.ReactNode
}

export const LevelPlayFinish = ({
  correctPercent,
  rewards,
  showBreakdown = false,
  items = [],
  actions
}: LevelPlayFinishProps) => {
  // O fallback vem do quiz: sem ele, percentual fora da faixa devolve
  // `undefined` e o componente estoura ao ler `.icon`.
  const tier = TIERS.find(({ score }) => correctPercent <= score) ?? TIERS[0]

  const { acquiredPoints, totalPoints, acquiredCoins, totalCoins } =
    rewards ?? {}
  const pointsValue = showBreakdown
    ? `${acquiredPoints}/${totalPoints}`
    : acquiredPoints
  const coinsValue = showBreakdown
    ? `${acquiredCoins}/${totalCoins}`
    : acquiredCoins

  return (
    <LevelPlayInterstitial
      className="max-w-[520px]"
      icon={
        <AppIcon name={tier.icon} className={`${tier.iconClassName} text-4xl`} />
      }
      title={tier.title}
      description={tier.description}
      action={actions}
    >
      <span className="text-caps text-gray-700 font-semibold tracking-wide uppercase">
        Você ganhou
      </span>

      <RewardBadge.Group>
        <RewardBadge type="xp" value={pointsValue} />
        <RewardBadge type="coins" value={coinsValue} label="Moedas" />
      </RewardBadge.Group>

      {showBreakdown && items.length > 0 && (
        <div className="w-full">
          <span className="block text-sm text-gray-700 font-semibold mb-2">
            Confira os seus acertos!
          </span>
          <div className="flex flex-col gap-1 w-full max-h-[175px] overflow-y-auto">
            {items.map(item => (
              <div
                key={item.id}
                className="flex items-center justify-between w-full pl-3 p-2 bg-gray-100 border border-gray-200 rounded-xl"
              >
                <span className="text-gray-800 text-xs font-bold text-left">
                  {item.name}
                </span>

                {item.correct ? (
                  <Tick02Icon
                    variant="stroke"
                    size={22}
                    className="text-success shrink-0"
                  />
                ) : (
                  <Cancel01Icon
                    variant="stroke"
                    size={22}
                    className="text-danger shrink-0"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </LevelPlayInterstitial>
  )
}
